import type { Dispatch, SetStateAction } from "react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { WithChildren } from "../../_metronic/helpers";
import type { IOrganization } from "../contexts/organizations/organizationsContext";
import { useGetUser } from "../hooks/reactQueryHooks/useUsers";

export interface IUser {
  name: string;
  email: string;
  avatar: string;
  role: string;
  created: string;
  firstName: string;
  lastName: string;
  organizations: IOrganization[];
  organization: IOrganization[];
  firstSignIn?: boolean;
  sub: string; // userId
  providers?: string[];
}

interface UserContextProps {
  user: IUser | null;
  setUser: Dispatch<SetStateAction<IUser | null>>;
  setUserOrganizations: (orgs: IOrganization[]) => void;
  setUpdatedUser: (args: any) => void;
  userOrganizations: IOrganization[] | null;
}

const initUserContextPropsState = {
  user: null,
  setUser: () => {},
  setUserOrganizations: () => {},
  setUpdatedUser: () => {},
  userOrganizations: null,
};

const UserContext = createContext<UserContextProps>(initUserContextPropsState);

export const useUser = () => {
  return useContext(UserContext);
};

export const UserProvider: React.FC<WithChildren> = ({ children }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<any>(null);

  const { isLoading, data } = useGetUser({
    userId: user?.sub || "",
    organizationId:
      (userOrganizations?.length && userOrganizations[0].id) || "",
  });

  useEffect(() => {
    if (isLoading) return;
    if (!data) return;
    setUser({
      ...user,
      ...data,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  const value = useMemo(() => {
    return {
      userOrganizations,
      user,
      setUser: (args: any) => {
        setUser({
          ...args.profile,
          providers: [],
        });
      },
      setUserOrganizations,
      setUpdatedUser: (args: IUser) => {
        setUser(prev => {
          return {
            ...prev,
            ...args,
          };
        });
      },
    };
  }, [userOrganizations, user, setUser, setUserOrganizations]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
